import { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  Clock,
  CheckCircle2,
  MoreHorizontal,
  Trash2,
  Edit,
  ListChecks,
  PackageOpen,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { yardApi, YardJobKit, YardJobKitLine } from '@/api/services/yard.api';
import { inventoryApi } from '@/api/services/inventory.api';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';

type JobKitStatus = 'DRAFT' | 'PENDING_PICK' | 'PICKING' | 'READY' | 'ISSUED' | 'RETURNED' | 'CANCELLED';

export default function YardJobKits() {
  const [kits, setKits] = useState<YardJobKit[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    draft: 0,
    pendingPick: 0,
    ready: 0,
    issued: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Create/Edit dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingKit, setEditingKit] = useState<YardJobKit | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    wellName: '',
    jobType: '',
    requiredDate: '',
  });

  // Add line item dialog
  const [isAddLineDialogOpen, setIsAddLineDialogOpen] = useState(false);
  const [selectedKitForLine, setSelectedKitForLine] = useState<YardJobKit | null>(null);
  const [lineFormData, setLineFormData] = useState({
    itemId: '',
    requestedQty: '',
    notes: '',
  });
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);

  // Kit details sheet
  const [selectedKit, setSelectedKit] = useState<YardJobKit | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Issue/Return dialogs
  const [isIssueDialogOpen, setIsIssueDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [kitToProcess, setKitToProcess] = useState<YardJobKit | null>(null);
  const [processNotes, setProcessNotes] = useState('');

  useEffect(() => {
    fetchKits();
    fetchStats();
  }, [statusFilter]);

  const fetchKits = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await yardApi.jobKits.getAll(params);
      setKits(response.data);
    } catch (error) {
      console.error('Error fetching job kits:', error);
      toast.error('Failed to load job kits');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await yardApi.jobKits.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await inventoryApi.getAll();
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const handleCreateKit = async () => {
    if (!formData.name) {
      toast.error('Kit name is required');
      return;
    }

    try {
      if (editingKit) {
        await yardApi.jobKits.update(editingKit.id, formData);
        toast.success('Job kit updated');
      } else {
        await yardApi.jobKits.create(formData);
        toast.success('Job kit created');
      }
      setIsCreateDialogOpen(false);
      setEditingKit(null);
      resetForm();
      fetchKits();
      fetchStats();
    } catch (error) {
      console.error('Error saving job kit:', error);
      toast.error('Failed to save job kit');
    }
  };

  const handleDeleteKit = async (kit: YardJobKit) => {
    if (!confirm('Are you sure you want to delete this job kit?')) return;

    try {
      await yardApi.jobKits.delete(kit.id);
      toast.success('Job kit deleted');
      fetchKits();
      fetchStats();
    } catch (error) {
      console.error('Error deleting job kit:', error);
      toast.error('Failed to delete job kit');
    }
  };

  const handleAddLineItem = async () => {
    if (!selectedKitForLine || !lineFormData.itemId || !lineFormData.requestedQty) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await yardApi.jobKits.addLine(selectedKitForLine.id, {
        itemId: lineFormData.itemId,
        requestedQty: parseFloat(lineFormData.requestedQty),
        notes: lineFormData.notes || undefined,
      });
      toast.success('Line item added');
      setIsAddLineDialogOpen(false);
      setSelectedKitForLine(null);
      setLineFormData({ itemId: '', requestedQty: '', notes: '' });
      fetchKits();
      // Refresh selected kit if viewing details
      if (selectedKit?.id === selectedKitForLine.id) {
        const response = await yardApi.jobKits.getById(selectedKit.id);
        setSelectedKit(response);
      }
    } catch (error) {
      console.error('Error adding line item:', error);
      toast.error('Failed to add line item');
    }
  };

  const handleGeneratePickList = async (kit: YardJobKit) => {
    try {
      await yardApi.jobKits.generatePickList(kit.id);
      toast.success('Pick list generated');
      fetchKits();
      fetchStats();
    } catch (error) {
      console.error('Error generating pick list:', error);
      toast.error('Failed to generate pick list');
    }
  };

  const handleIssueKit = async () => {
    if (!kitToProcess) return;

    try {
      await yardApi.jobKits.issue(kitToProcess.id, processNotes || undefined);
      toast.success('Job kit issued');
      setIsIssueDialogOpen(false);
      setKitToProcess(null);
      setProcessNotes('');
      fetchKits();
      fetchStats();
    } catch (error) {
      console.error('Error issuing kit:', error);
      toast.error('Failed to issue kit');
    }
  };

  const handleReturnKit = async () => {
    if (!kitToProcess) return;

    try {
      // Return all items with their issued quantities
      const items = kitToProcess.lines.map((line) => ({
        lineId: line.id,
        returnedQty: line.issuedQty,
      }));
      await yardApi.jobKits.returnItems(kitToProcess.id, items, processNotes || undefined);
      toast.success('Job kit returned');
      setIsReturnDialogOpen(false);
      setKitToProcess(null);
      setProcessNotes('');
      fetchKits();
      fetchStats();
    } catch (error) {
      console.error('Error returning kit:', error);
      toast.error('Failed to return kit');
    }
  };

  const openEditDialog = (kit: YardJobKit) => {
    setEditingKit(kit);
    setFormData({
      name: kit.name,
      description: kit.description || '',
      wellName: kit.wellName || '',
      jobType: kit.jobType || '',
      requiredDate: kit.requiredDate ? kit.requiredDate.split('T')[0] : '',
    });
    setIsCreateDialogOpen(true);
  };

  const openAddLineDialog = async (kit: YardJobKit) => {
    setSelectedKitForLine(kit);
    await fetchInventoryItems();
    setIsAddLineDialogOpen(true);
  };

  const viewKitDetails = async (kit: YardJobKit) => {
    try {
      const response = await yardApi.jobKits.getById(kit.id);
      setSelectedKit(response);
      setIsSheetOpen(true);
    } catch (error) {
      console.error('Error fetching kit details:', error);
      toast.error('Failed to load kit details');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      wellName: '',
      jobType: '',
      requiredDate: '',
    });
  };

  const getStatusBadge = (status: JobKitStatus) => {
    const config: Record<JobKitStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      DRAFT: { variant: 'secondary', label: 'Draft' },
      PENDING_PICK: { variant: 'outline', label: 'Pending Pick' },
      PICKING: { variant: 'default', label: 'Picking' },
      READY: { variant: 'default', label: 'Ready' },
      ISSUED: { variant: 'outline', label: 'Issued' },
      RETURNED: { variant: 'secondary', label: 'Returned' },
      CANCELLED: { variant: 'destructive', label: 'Cancelled' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const filteredKits = kits.filter(kit => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesKitNumber = kit.kitNumber?.toLowerCase().includes(query);
      const matchesName = kit.name?.toLowerCase().includes(query);
      const matchesWell = kit.wellName?.toLowerCase().includes(query);
      if (!matchesKitNumber && !matchesName && !matchesWell) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Job Kits"
        description="Manage job kits for well operations"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchKits(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => { resetForm(); setEditingKit(null); setIsCreateDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Kit
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Draft Kits"
          value={stats.draft}
          icon={<Edit className="h-5 w-5" />}
        />
        <MetricCard
          label="Pending Pick"
          value={stats.pendingPick}
          icon={<Clock className="h-5 w-5" />}
        />
        <MetricCard
          label="Ready for Issue"
          value={stats.ready}
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
        <MetricCard
          label="Issued"
          value={stats.issued}
          icon={<PackageOpen className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by kit number, name, or well..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PENDING_PICK">Pending Pick</SelectItem>
            <SelectItem value="PICKING">Picking</SelectItem>
            <SelectItem value="READY">Ready</SelectItem>
            <SelectItem value="ISSUED">Issued</SelectItem>
            <SelectItem value="RETURNED">Returned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kits Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kit #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Well / Job</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Required Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading job kits...
                  </TableCell>
                </TableRow>
              ) : filteredKits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No job kits found
                  </TableCell>
                </TableRow>
              ) : (
                filteredKits.map((kit) => (
                  <TableRow key={kit.id}>
                    <TableCell className="font-medium">{kit.kitNumber}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{kit.name}</p>
                        {kit.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {kit.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {kit.wellName && <p>{kit.wellName}</p>}
                        {kit.jobType && (
                          <Badge variant="outline" className="mt-1">
                            {kit.jobType}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{kit.lines?.length || 0} items</TableCell>
                    <TableCell>
                      {kit.requiredDate
                        ? new Date(kit.requiredDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(kit.status as JobKitStatus)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewKitDetails(kit)}>
                            View Details
                          </DropdownMenuItem>
                          {kit.status === 'DRAFT' && (
                            <>
                              <DropdownMenuItem onClick={() => openEditDialog(kit)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openAddLineDialog(kit)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Items
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleGeneratePickList(kit)}>
                                <ListChecks className="h-4 w-4 mr-2" />
                                Generate Pick List
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDeleteKit(kit)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                          {kit.status === 'READY' && (
                            <DropdownMenuItem
                              onClick={() => {
                                setKitToProcess(kit);
                                setIsIssueDialogOpen(true);
                              }}
                            >
                              <PackageOpen className="h-4 w-4 mr-2" />
                              Issue Kit
                            </DropdownMenuItem>
                          )}
                          {kit.status === 'ISSUED' && (
                            <DropdownMenuItem
                              onClick={() => {
                                setKitToProcess(kit);
                                setIsReturnDialogOpen(true);
                              }}
                            >
                              <Undo2 className="h-4 w-4 mr-2" />
                              Return Kit
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingKit ? 'Edit Job Kit' : 'Create Job Kit'}</DialogTitle>
            <DialogDescription>
              {editingKit
                ? 'Update the job kit information.'
                : 'Create a new job kit for well operations.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Kit Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter kit name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Kit description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wellName">Well Name</Label>
                <Input
                  id="wellName"
                  value={formData.wellName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, wellName: e.target.value }))
                  }
                  placeholder="Well name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobType">Job Type</Label>
                <Select
                  value={formData.jobType}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, jobType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRILLING">Drilling</SelectItem>
                    <SelectItem value="COMPLETION">Completion</SelectItem>
                    <SelectItem value="WORKOVER">Workover</SelectItem>
                    <SelectItem value="INTERVENTION">Intervention</SelectItem>
                    <SelectItem value="ABANDONMENT">Abandonment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="requiredDate">Required Date</Label>
              <Input
                id="requiredDate"
                type="date"
                value={formData.requiredDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, requiredDate: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKit}>
              {editingKit ? 'Update Kit' : 'Create Kit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Line Item Dialog */}
      <Dialog open={isAddLineDialogOpen} onOpenChange={setIsAddLineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Item to Kit</DialogTitle>
            <DialogDescription>
              Add an inventory item to the job kit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="itemId">Inventory Item *</Label>
              <Select
                value={lineFormData.itemId}
                onValueChange={(value) =>
                  setLineFormData((prev) => ({ ...prev, itemId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.sku} - {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="requestedQty">Requested Quantity *</Label>
              <Input
                id="requestedQty"
                type="number"
                min="1"
                value={lineFormData.requestedQty}
                onChange={(e) =>
                  setLineFormData((prev) => ({ ...prev, requestedQty: e.target.value }))
                }
                placeholder="Enter quantity"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lineNotes">Notes</Label>
              <Input
                id="lineNotes"
                value={lineFormData.notes}
                onChange={(e) =>
                  setLineFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddLineDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLineItem}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={isIssueDialogOpen} onOpenChange={setIsIssueDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Job Kit</DialogTitle>
            <DialogDescription>
              Issue this job kit for use. This will mark all items as issued.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="issueNotes">Notes</Label>
              <Textarea
                id="issueNotes"
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
                placeholder="Optional notes about the issuance..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIssueDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleIssueKit}>
              <PackageOpen className="h-4 w-4 mr-2" />
              Issue Kit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Job Kit</DialogTitle>
            <DialogDescription>
              Process the return of this job kit. Items will be returned to inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="returnNotes">Notes</Label>
              <Textarea
                id="returnNotes"
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
                placeholder="Notes about returned items, condition, etc..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReturnDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReturnKit}>
              <Undo2 className="h-4 w-4 mr-2" />
              Return Kit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kit Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Job Kit Details</SheetTitle>
            <SheetDescription>
              View kit information and line items
            </SheetDescription>
          </SheetHeader>
          {selectedKit && (
            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Kit Number</span>
                  <span className="font-medium">{selectedKit.kitNumber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Name</span>
                  <span className="font-medium">{selectedKit.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {getStatusBadge(selectedKit.status as JobKitStatus)}
                </div>
                {selectedKit.wellName && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Well</span>
                    <span>{selectedKit.wellName}</span>
                  </div>
                )}
                {selectedKit.jobType && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Job Type</span>
                    <Badge variant="outline">{selectedKit.jobType}</Badge>
                  </div>
                )}
                {selectedKit.requiredDate && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Required Date</span>
                    <span>{new Date(selectedKit.requiredDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Kit Items ({selectedKit.lines?.length || 0})</h4>
                {selectedKit.lines && selectedKit.lines.length > 0 ? (
                  <div className="space-y-2">
                    {selectedKit.lines.map((line: YardJobKitLine) => (
                      <div
                        key={line.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{line.item?.name || 'Unknown Item'}</p>
                          <p className="text-sm text-muted-foreground">
                            {line.item?.sku}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{line.requestedQty}</p>
                          <p className="text-sm text-muted-foreground">
                            Picked: {line.pickedQty} | Issued: {line.issuedQty}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No items in this kit</p>
                )}
              </div>

              {selectedKit.notes && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedKit.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
